import flagsmith, { IFlagsmith } from 'flagsmith'
import { App, ref } from 'vue'
import type { FeatureFlagKey } from './flagsmith-flags'
export { FeatureFlags, type FeatureFlagKey } from './flagsmith-flags'

export type FlagSmithPluginOptions = {
  environmentID: string
  cacheFlags: boolean
  enableAnalytics: boolean
}

export const FlagsmithKey = Symbol('flagsmith')

/**
 * Reactive signal that flips to `true` once Flagsmith's initial flag fetch
 * settles (success or failure), or immediately when there's no SDK key
 * (e2e/dev — flags are mocked externally). Await this before reading
 * `hasFeature` so an unloaded flag isn't mistaken for a disabled one.
 */
export const flagsReady = ref(false)

const flagSmithPlugin = {
  install: (app: App) => {
    const context: IFlagsmith<FeatureFlagKey> = flagsmith

    // Provide immediately (synchronous) so inject always finds it
    app.config.globalProperties.$flagsmith = context
    app.provide(FlagsmithKey, context)

    // No SDK key (e2e/dev) — skip init. Mirrors the segment/customerio plugins'
    // `if (!writeKey) return` guard: without this, `flagsmith.init` throws
    // "non-empty `apiKey`" and the uncaught rejection surfaces as a pageerror.
    const environmentID = import.meta.env.VITE_FLAGSMITH_SDK_KEY
    if (!environmentID) {
      flagsReady.value = true
      return
    }

    // Initialize in background; swallow failures so a blocked/bad init can't
    // crash the app or surface an uncaught pageerror.
    flagsmith
      .init({
        environmentID,
        cacheFlags: true,
        enableAnalytics: false,
      })
      .catch(() => {})
      .finally(() => {
        flagsReady.value = true
      })
  },
}

export default flagSmithPlugin
